import store, { IAppState } from "../Store";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import Datastore from "../../communication/Datastore";
import Utilities from "../../foundation/Utility";
import TTSDebouncer from "../../foundation/TTSDebouncer";
import User from "../../types/core/User";
import PhoneNumber from "../../types/core/PhoneNumber";
import Address from "../../types/core/Address";
import EmailAddress from "../../types/core/EmailAddress";
import PermissionSet from "../../types/core/PermissionSet";
import TwoFactorSettings from "../../types/twoFactor/TwoFactorSettings";
import TwoFactorDisplayEntry from "../../types/twoFactor/TwoFactorDisplayEntry";

import { REHYDRATE } from "redux-persist";
import UserDigest from "../../types/core/UserDigest";
import { recalcRoles } from "./UserRoles";
import { ImportLog } from "../../types/core/ImportLog";
import { User_ImportOptions } from "../../types/core/User_ImportOptions";
import { unwrappedDispatch } from "../../foundation/Dispatching";
import AzImportMapping from "../../types/core/AzImportMapping";
import { fetchClientSystems, fetchClientSystemVersions } from "./ClientSystem";
import { initialiseSettingsDetails, setPreference } from "../system/Settings";
import { ClientSystemPreference } from "../../types/core/ClientSystemVersion";
import { updateSessionProfilePicture } from "../system/Session";

let debouncer = new TTSDebouncer(500);

// Define the type for our state store entry
type UserState = {
    profileUser: User;
    profilePicture: string;
    systemVersions: ClientSystemPreference[];
    systemVersionsStatus: string;
    status: string;
    allUsers: User[];
    allUsersStatus: string;
    allUsersMultisite: Record<number, boolean>;
    allUsersMultisiteStatus: string;
    digest: UserDigest[];
    digestStatus: string;
    contactStatus: string;
    passwordStatus: string;
    passwordConfig: number;
    twoFactorStatus: string;
    twoFactorOptions: Record<number, string>;
    twoFactorSettings: TwoFactorSettings;
    twoFactorDisplay: TwoFactorDisplayEntry[];
    hasSSO: boolean;
    error: string;
};

// Define the initial state for our state store entry
const initialState: UserState = {
    profileUser: null,
    profilePicture: null,
    status: "idle",
    systemVersions: [],
    systemVersionsStatus: "idle",
    allUsers: null,
    allUsersStatus: "idle",
    allUsersMultisite: null,
    allUsersMultisiteStatus: "idle",
    digest: [],
    digestStatus: "idle",
    contactStatus: "idle",
    passwordStatus: "idle",
    passwordConfig: -1,
    twoFactorStatus: "idle",
    twoFactorOptions: null,
    twoFactorSettings: null,
    twoFactorDisplay: [],
    hasSSO: false,
    error: null,
};

// Create a slice to hold all our reducers
const userSlice = createSlice({
    name: "user",
    initialState: initialState,
    reducers: {
        updateUserDetails: (state, action: PayloadAction<User>) => {
            state.profileUser = new User(
                Utilities.merge(state.profileUser, action.payload),
            );
        },
        addAllUserDetails: (state, action: PayloadAction<User>) => {
            state.allUsers.push(new User(action.payload));
        },
        updateAllUserDetails: (state, action: PayloadAction<User>) => {
            let user = state.allUsers.find(
                user => user.id === action.payload.id,
            );

            if (user) Utilities.merge(user, action.payload);
        },
        deleteAllUserDetails: (state, action: PayloadAction<User[]>) => {
            action.payload.forEach(user => {
                let entry = state.allUsers.find(entry => entry.id === user.id);
                if (entry)
                    state.allUsers.splice(state.allUsers.indexOf(entry), 1);
            });
        },
        updateUserProfilePicture: (state, action: PayloadAction<string>) => {
            state.profilePicture = action.payload;
        },
        updateTwoFactorType: (state, action: PayloadAction<string>) => {
            state.twoFactorSettings.enabledTypes.forEach(entry => {
                entry.enabled = entry.type === action.payload;
            });
            state.twoFactorDisplay = [];
            Object.entries(state.twoFactorOptions).forEach(entry => {
                state.twoFactorDisplay.push({
                    value: Number(entry[0]),
                    name: entry[1],
                    enabled: !!state.twoFactorSettings.enabledTypes.find(
                        item => item.type === entry[1] && item.enabled,
                    ),
                });
            });
        },
        updatePasswordless: (state, action: PayloadAction<boolean>) => {
            state.profileUser.hasFido2 = action.payload;
        },
        updatePasswordConfig: (state, action: PayloadAction<number>) => {
            state.passwordConfig = action.payload;
        },
        updateVersionPreference: (
            state,
            action: PayloadAction<{ id: number; value: string }>,
        ) => {
            const { id, value } = action.payload;
            const target = state.systemVersions.find(entry => entry.id === id);
            if (!target) return;

            const update = {
                ...target,
                versions: [...target.versions.map(version => ({ ...version }))],
            };
            for (const version of update.versions)
                version.preferred = version.name === value;
            state.systemVersions = [
                ...state.systemVersions.filter(entry => entry !== target),
                update,
            ];
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;

                const user = rawData?.payload?.user?.user
                    ? new User(rawData?.payload?.user.user)
                    : null;
                const allUsers = rawData?.payload?.user?.allUsers
                    ? rawData?.payload?.user?.allUsers.map(
                          user => new User(user),
                      )
                    : null;

                return {
                    profileUser: user,
                    profilePicture: rawData?.payload?.user?.profilePicture,
                    systemVersions:
                        rawData?.payload?.user?.systemVersions ?? [],
                    systemVersionsStatus:
                        rawData?.payload?.user?.systemVersionsStatus ?? "idle",
                    status: !user ? "idle" : rawData?.payload?.user?.status,
                    allUsers: allUsers,
                    allUsersStatus:
                        !allUsers || !allUsers.length
                            ? "idle"
                            : rawData?.payload?.user?.allUsersStatus ===
                              "loading"
                            ? "idle"
                            : rawData?.payload?.user?.allUsersStatus,
                    allUsersMultisite:
                        rawData?.payload?.user?.allUsersMultisite,
                    allUsersMultisiteStatus: !rawData?.payload?.user
                        ?.allUsersMultisite
                        ? "idle"
                        : rawData?.payload?.user?.allUsersMultisiteStatus,
                    digest: rawData?.payload?.user?.digest
                        ? rawData?.payload?.user?.digest.map(
                              user => new UserDigest(user),
                          )
                        : [],
                    digestStatus:
                        !rawData?.payload?.user?.digest ||
                        !rawData?.payload?.user?.digest.length
                            ? "idle"
                            : rawData?.payload?.user?.digestStatus,
                    contactStatus:
                        !user ||
                        !user.numbers ||
                        !user.emails ||
                        !user.addresses
                            ? "idle"
                            : rawData?.payload?.user?.contactStatus,
                    passwordStatus: !rawData?.payload?.user?.passwordConfig
                        ? "idle"
                        : rawData?.payload?.user?.passwordStatus,
                    passwordConfig: rawData?.payload?.user?.passwordConfig,
                    twoFactorStatus:
                        !rawData?.payload?.user?.twoFactorOptions ||
                        !rawData?.payload?.user?.twoFactorSettings ||
                        !rawData?.payload?.user?.twoFactorDisplay
                            ? "idle"
                            : rawData?.payload?.user?.twoFactorStatus,
                    twoFactorOptions: rawData?.payload?.user?.twoFactorOptions,
                    twoFactorSettings:
                        rawData?.payload?.user?.twoFactorSettings,
                    twoFactorDisplay:
                        rawData?.payload?.user?.twoFactorDisplay ?? [],
                    hasSSO: rawData?.payload?.user?.hasSSO,
                    error: null,
                };
            })
            .addCase(fetchAllUserDetails.pending, (state, action) => {
                state.allUsersStatus = "loading";
            })
            .addCase(fetchAllUserDetails.fulfilled, (state, action) => {
                state.allUsersStatus = "succeeded";
                // Add any fetched posts to the array
                action.payload.forEach(
                    user =>
                        (user.fullName = [
                            user.firstName,
                            user.middleName,
                            user.lastName,
                        ]
                            .filter(name => !!name)
                            .join(" ")),
                );

                state.allUsers = action.payload.map(item => new User(item));
            })
            .addCase(fetchAllUserDetails.rejected, (state, action) => {
                state.allUsersStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchUserContactDetails.pending, (state, action) => {
                state.contactStatus = "loading";
            })
            .addCase(fetchUserContactDetails.fulfilled, (state, action) => {
                state.contactStatus = "succeeded";
                // Add any fetched posts to the array
                state.profileUser = new User({
                    ...action.payload[0],
                    numbers:
                        action.payload[1]?.map(item => new PhoneNumber(item)) ??
                        [],
                    emails:
                        action.payload[2]?.map(
                            item => new EmailAddress(item),
                        ) ?? [],
                    addresses:
                        action.payload[3]?.map(item => new Address(item)) ?? [],
                });
            })
            .addCase(fetchUserContactDetails.rejected, (state, action) => {
                state.contactStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchTwoFactorDetails.pending, (state, action) => {
                state.twoFactorStatus = "loading";
            })
            .addCase(fetchTwoFactorDetails.fulfilled, (state, action) => {
                state.twoFactorStatus = "succeeded";
                // Add any fetched posts to the array
                state.twoFactorOptions = action.payload[0];
                state.twoFactorSettings = action.payload[1];
                state.hasSSO = action.payload[2];
                state.twoFactorDisplay = [];
                Object.entries(state.twoFactorOptions).forEach(entry => {
                    state.twoFactorDisplay.push({
                        value: Number(entry[0]),
                        name: entry[1],
                        enabled: !!state.twoFactorSettings.enabledTypes.find(
                            item => item.type === entry[1] && item.enabled,
                        ),
                    });
                });
            })
            .addCase(fetchTwoFactorDetails.rejected, (state, action) => {
                state.twoFactorStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchUserPasswordDetails.pending, (state, action) => {
                state.passwordStatus = "loading";
            })
            .addCase(fetchUserPasswordDetails.fulfilled, (state, action) => {
                state.passwordStatus = "succeeded";
                // Add any fetched posts to the array
                state.passwordConfig = action.payload;
            })
            .addCase(fetchUserPasswordDetails.rejected, (state, action) => {
                state.passwordStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchMissingDigestDetails.pending, (state, action) => {
                state.digestStatus = "loading";
            })
            .addCase(fetchMissingDigestDetails.fulfilled, (state, action) => {
                state.digestStatus = "succeeded";
                // Add any fetched posts to the array
                state.digest = state.digest.concat(
                    action.payload.map(item => new UserDigest(item)),
                );
            })
            .addCase(fetchMissingDigestDetails.rejected, (state, action) => {
                state.digestStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(initialiseVersionPreferences.pending, (state, action) => {
                state.systemVersionsStatus = "loading";
            })
            .addCase(
                initialiseVersionPreferences.fulfilled,
                (state, action) => {
                    state.systemVersions = action.payload;
                    state.systemVersionsStatus = "succeeded";
                },
            )
            .addCase(initialiseVersionPreferences.rejected, (state, action) => {
                state.systemVersionsStatus = "failed";
                state.error = action.error.message;
            });
    },
});

const {
    updateUserDetails,
    addAllUserDetails,
    updateAllUserDetails,
    deleteAllUserDetails,
    updateTwoFactorType,
    updatePasswordless,
    updatePasswordConfig,
    updateVersionPreference,
} = userSlice.actions;
export const { updateUserProfilePicture } = userSlice.actions;

/******* User Management *******/
const fetchAllUserDetails = createAsyncThunk(
    "user/fetchAllUserDetails",
    async () => {
        return await Datastore.getType<User[]>("api/Users");
    },
);
export const initialiseAllUserDetails = createAsyncThunk(
    "user/initialiseAllUserDetails",
    async (_, thunkAPI): Promise<void> => {
        await thunkAPI.dispatch(fetchAllUserDetails());
    },
    {
        condition: (_, { getState }) => {
            const { user } = (getState() as IAppState).db;
            return user.allUsersStatus !== "loading";
        },
    },
);
export const getAllUsers = createAsyncThunk(
    "user/getAllUsers",
    async (_, thunkAPI): Promise<User[]> => {
        await thunkAPI.dispatch(initialiseAllUserDetails());

        return store.getState().db.user.allUsers;
    },
);
export const updateUser = createAsyncThunk(
    "user/updateUser",
    async (users: User[], thunkAPI) => {
        for (const user of users) {
            user.editedOn = new Date();
            thunkAPI.dispatch(updateUserDetails(user));
        }
        debouncer.call(async () => {
            let response = await Datastore.putType<User[]>("api/Users", users);
            thunkAPI.dispatch(updateUserDetails(response[0]));
        });

        thunkAPI.dispatch(recalcRoles(store.getState().db.user.allUsers));
    },
);
export const addAllUser = createAsyncThunk(
    "user/addAllUser",
    async (users: User[], thunkAPI) => {
        debouncer.call(async () => {
            const response = await Datastore.postType<User[]>(
                "api/Users",
                users,
            );
            thunkAPI.dispatch(addAllUserDetails(response[0]));
        });

        thunkAPI.dispatch(recalcRoles(store.getState().db.user.allUsers));
    },
);
export const updateAllUser = createAsyncThunk(
    "user/updateAllUser",
    async (users: User[], thunkAPI) => {
        for (const user of users) {
            user.editedOn = new Date();
            thunkAPI.dispatch(updateAllUserDetails(user));
        }
        debouncer.call(async () => {
            let response = await Datastore.putType<User[]>("api/Users", users);
            thunkAPI.dispatch(updateAllUserDetails(response[0]));
        });

        thunkAPI.dispatch(recalcRoles(store.getState().db.user.allUsers));
    },
);
interface UserProfilePictureUpdate {
    userId: number;
    image: string;
}
export const updateUserPicture = createAsyncThunk(
    "user/updateUserPicture",
    async (payload: UserProfilePictureUpdate, thunkAPI) => {
        await Datastore.post(
            `api/Users/${payload.userId}/UpdateProfilePicture`,
            payload.image,
        );
        thunkAPI.dispatch(updateUserProfilePicture(payload.image));
        const { session } = store.getState().session;
        if (session?.user?.id === payload.userId)
            thunkAPI.dispatch(updateSessionProfilePicture(payload.image));
    },
);
export const deleteUsers = createAsyncThunk(
    "user/deleteUsers",
    async (users: User[], thunkAPI) => {
        await Datastore.post(
            "/api/Users/Delete",
            users.map(user => user.id),
        );
        thunkAPI.dispatch(deleteAllUserDetails(users));
    },
);

/******* Password Management *******/
const fetchUserPasswordDetails = createAsyncThunk(
    "user/fetchUserPasswordDetails",
    async () => {
        const response = await Datastore.getType<number>(
            "api/Authentication/Session/PasswordRequirement",
        );
        return response;
    },
    {
        condition: (_, { getState }) => {
            const { user } = (getState() as IAppState).db;
            return user.passwordStatus === "idle";
        },
    },
);
export const initialiseUserPasswordDetails = createAsyncThunk(
    "user/initialiseUserPasswordDetails",
    async (_, thunkAPI) => {
        await thunkAPI.dispatch(fetchUserPasswordDetails());
    },
);
export const updateUserPasswordDetails = createAsyncThunk(
    "user/updateUserPasswordDetails",
    async (payload: number, thunkAPI) => {
        await Datastore.post(
            "api/Settings/PasswordComplexity",
            payload.toString(),
        );
        thunkAPI.dispatch(updatePasswordConfig(payload));
    },
);

/******* Two Factor Management *******/
export const fetchTwoFactorDetails = createAsyncThunk(
    "user/fetchTwoFactorDetails",
    async (user: User) => {
        const response = await Promise.all([
            Datastore.getType<Record<number, string>>(
                "api/Authentication/Session/TwoFactorAvailable",
            ),
            Datastore.getType<TwoFactorSettings>(
                `api/Security/User/${user.id}/TwoFactor`,
            ),
            Datastore.getType<boolean>("api/Authentication/Session/IsSSO"),
        ]);
        return response;
    },
    {
        condition: (_, { getState }) => {
            const { user } = (getState() as IAppState).db;
            return user.twoFactorStatus === "idle";
        },
    },
);
export const updateUserTwoFactorType = createAsyncThunk(
    "user/updateTwoFactorType",
    async (type: TwoFactorDisplayEntry, thunkAPI) => {
        thunkAPI.dispatch(updateTwoFactorType(type?.name));
    },
);

/******* User Contact Management *******/
export const fetchUserContactDetails = createAsyncThunk(
    "user/fetchUserContactDetails",
    async (targetUser: User) => {
        const response = await Promise.all([
            targetUser,
            Datastore.getType<PhoneNumber[]>(
                `api/PhoneNumbers/People/${targetUser.personID}`,
            ),
            Datastore.getType<EmailAddress[]>(
                `api/EmailAddresses/People/${targetUser.personID}`,
            ),
            Datastore.getType<Address[]>(
                `api/Addresses/People/${targetUser.personID}`,
            ),
        ]);
        return response;
    },
);

/******* Passwordless Management *******/
export const updateUserPasswordless = createAsyncThunk(
    "user/initialiseTwoFactorDetails",
    async (value: boolean, thunkAPI) => {
        await thunkAPI.dispatch(updatePasswordless(value));
    },
);

/******* Digest Management *******/
const fetchMissingDigestDetails = createAsyncThunk(
    "user/fetchMissingDigestDetails",
    async (userIds: number[]) => {
        return await Datastore.postType<UserDigest[]>(
            "api/Users/Digest",
            userIds,
        );
    },
);
export const getDigestDetails = createAsyncThunk(
    "user/getDigestDetails",
    async (userIds: number[], thunkAPI): Promise<UserDigest[]> => {
        let user = store.getState().db.user;
        const existingIds = user.digest.map(user => user.id);
        const missingIds = userIds.filter(
            userId => !existingIds.includes(userId),
        );

        if (missingIds.length)
            await thunkAPI.dispatch(fetchMissingDigestDetails(missingIds));

        user = store.getState().db.user;
        return user.digest.filter(user => userIds.includes(user.id));
    },
);

/*******     Importing     *******/
export const importUsersFromTD = async (
    id: number,
    options: User_ImportOptions,
) => {
    const result = await Datastore.postType<ImportLog>(
        `api/Users/Manage/Import/TD/${id}`,
        options,
    );

    await unwrappedDispatch(fetchAllUserDetails());

    return result;
};
export const importUsersFromAzr = async (options: AzImportMapping) => {
    const result = await Datastore.postType<ImportLog>(
        `api/Users/Manage/Import/Azure`,
        options,
    );

    await unwrappedDispatch(fetchAllUserDetails());

    return result;
};

/*******    Activations    *******/
export const sendActivations = async (userIds: number[]) => {
    await Datastore.post("api/Users/Activations", userIds);
};

// We have to export a method to use use our thunk for getting activity data
export function CreatePermissionSet(permissions: PermissionSet) {
    let result = new PermissionSet();
    result.init(permissions);
    return result;
}

export const initialiseVersionPreferences = createAsyncThunk(
    "user/initialiseVersionPreferences",
    async (_, thunkAPI): Promise<ClientSystemPreference[]> => {
        thunkAPI.dispatch(fetchClientSystems());
        thunkAPI.dispatch(fetchClientSystemVersions());
        thunkAPI.dispatch(initialiseSettingsDetails());

        const started = new Date();
        await Utilities.waitOn(() => {
            const { clientSystem, settings } = store.getState().db;
            return (
                new Date().getTime() - started.getTime() > 30000 ||
                (clientSystem.systemsStatus === "succeeded" &&
                    clientSystem.versionsStatus === "succeeded" &&
                    settings.preferencesStatus === "succeeded")
            );
        });

        const { clientSystem, settings } = store.getState().db;

        if (
            clientSystem.systemsStatus !== "succeeded" ||
            clientSystem.versionsStatus !== "succeeded" ||
            settings.preferencesStatus !== "succeeded"
        )
            throw new Error(
                "Failed to load client system versions or preferences within 30 seconds",
            );

        const result: ClientSystemPreference[] = [];
        for (const system of clientSystem.clientSystems) {
            const out: ClientSystemPreference = {
                id: system.id,
                name: system.name,
                versions: [],
            };
            const preference = settings.preferences[`System_${system.id}`];

            for (const version of clientSystem.versions) {
                if (version.clientSystemID !== system.id) continue;

                out.versions.push({
                    name: version.version,
                    preferred:
                        preference === version.version ||
                        (!preference && version.default),
                });
            }

            if (out.versions.length) result.push(out);
        }

        return result;
    },
    {
        condition: (_, { getState }) => {
            const { user } = (getState() as IAppState).db;
            return user.systemVersionsStatus !== "loading";
        },
    },
);
export const setVersionPreference = async (id: number, value: string) => {
    store.dispatch(
        updateVersionPreference({
            id,
            value,
        }),
    );
    await setPreference(`System_${id}`, value);
};

// We have to export the reducer as this is added to the state store
export default userSlice.reducer;
